import React from "react";
import styled from "styled-components";
import { T, FONTS, FS } from "../../HearingBundles/tokens";
import { Icon } from "../components/Icons";
import { getPageCount } from "../CreateTrialBundleModal";

const Step4Review = ({
  name,
  bundleType,
  bundleConfigs,
  hearing,
  bundles,
  pickedDocIds,
  totalAssigned,
  totalTabs,
  checked,
  unassignedDocEntries = [],
}) => {
  const unassignedPageCount = unassignedDocEntries.reduce(
    (sum, a) => sum + getPageCount(a.pages),
    0,
  );
  const grandTotalPages = totalAssigned + unassignedPageCount;
  const grandTotalTabs = totalTabs + unassignedDocEntries.length;

  return (
    <Container>
      <Section>
        <SectionLabel>SUMMARY</SectionLabel>
        <SummaryCard>
          <SummaryGrid>
            <SummaryField>
              <FieldMeta>Bundle Name</FieldMeta>
              <FieldValue>{name || "—"}</FieldValue>
            </SummaryField>
            <SummaryField>
              <FieldMeta>Bundle Type</FieldMeta>
              <FieldValue>{bundleType || "—"}</FieldValue>
            </SummaryField>
            <SummaryField>
              <FieldMeta>Hearing / Purpose</FieldMeta>
              <FieldValue>{hearing || "—"}</FieldValue>
            </SummaryField>
            <SummaryField>
              <FieldMeta>Sub-Bundles</FieldMeta>
              <FieldValue>{checked ? bundles.length : "—"}</FieldValue>
            </SummaryField>
            <SummaryField>
              <FieldMeta>Tabs</FieldMeta>
              <FieldValue>{grandTotalTabs}</FieldValue>
            </SummaryField>
            <SummaryField>
              <FieldMeta>Source Documents</FieldMeta>
              <FieldValue>{pickedDocIds.length}</FieldValue>
            </SummaryField>
          </SummaryGrid>

          <TotalPagesBanner>
            <Icon.File size={22} color={T.teal} />
            <TotalPagesNum>{grandTotalPages}</TotalPagesNum>
            <TotalPagesMeta>
              total pages across{" "}
              <strong>
                {grandTotalTabs} tab{grandTotalTabs !== 1 ? "s" : ""}
              </strong>
              {checked && (
                <>
                  {" "}
                  in{" "}
                  <strong>
                    {bundles.length} bundle{bundles.length !== 1 ? "s" : ""}
                    {unassignedDocEntries.length > 0 ? " + unassigned" : ""}
                  </strong>
                </>
              )}
            </TotalPagesMeta>
          </TotalPagesBanner>
        </SummaryCard>
      </Section>

      {checked && (
        <Section>
          <SectionLabel>SUB-BUNDLE STRUCTURE</SectionLabel>
          <BundleList>
            {bundles.map((b) => {
              const tabCount = new Set(b.assignments.map((a) => a.tabNum)).size;
              const pageCount = b.assignments.reduce(
                (sum, a) => sum + getPageCount(a.pages),
                0,
              );
              return (
                <BundleRow key={b.code}>
                  <Icon.FolderOpen size={15} color={T.tealDark} />
                  <BundleRowName>
                    {b.prefix || b.code} — {b.name}
                  </BundleRowName>
                  <BundleRowMeta>
                    {tabCount} tab{tabCount !== 1 ? "s" : ""} · {pageCount} page
                    {pageCount !== 1 ? "s" : ""}
                  </BundleRowMeta>
                </BundleRow>
              );
            })}

            {unassignedDocEntries.length > 0 && (
              <UnassignedBlock>
                <UnassignedHeader>
                  <Icon.File size={14} color={T.mute} />
                  <UnassignedTitle>
                    Without sub-folder ({unassignedDocEntries.length} document
                    {unassignedDocEntries.length !== 1 ? "s" : ""})
                  </UnassignedTitle>
                  <BundleRowMeta>
                    {unassignedDocEntries.length} tab
                    {unassignedDocEntries.length !== 1 ? "s" : ""} ·{" "}
                    {unassignedPageCount} page
                    {unassignedPageCount !== 1 ? "s" : ""}
                  </BundleRowMeta>
                </UnassignedHeader>
                <UnassignedDocList>
                  {unassignedDocEntries.map((entry) => {
                    const pgCount = getPageCount(entry.pages);
                    return (
                      <UnassignedDocItem key={entry.sourceDocId}>
                        <Icon.File size={12} color={T.mute} />
                        <UnassignedDocName title={entry.userLabel}>
                          {entry.userLabel}
                        </UnassignedDocName>
                        <UnassignedDocPages>
                          {pgCount} page{pgCount !== 1 ? "s" : ""}
                        </UnassignedDocPages>
                      </UnassignedDocItem>
                    );
                  })}
                </UnassignedDocList>
              </UnassignedBlock>
            )}
          </BundleList>
        </Section>
      )}
    </Container>
  );
};

export default Step4Review;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionLabel = styled.div`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${T.mute};
  text-transform: uppercase;
`;

const SummaryCard = styled.div`
  border: 1.5px solid ${T.line};
  border-radius: 14px;
  overflow: hidden;
  background: white;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border-bottom: 1px solid ${T.line};
  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const SummaryField = styled.div`
  padding: 16px 20px;
  border-right: 1px solid ${T.line};
  &:nth-child(3n) {
    border-right: none;
  }
`;

const FieldMeta = styled.div`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  color: ${T.mute};
  margin-bottom: 4px;
`;

const FieldValue = styled.div`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.md};
  font-weight: 700;
  color: ${T.navy};
`;

const TotalPagesBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  background: ${T.tealTint};
`;

const TotalPagesNum = styled.span`
  font-family: ${FONTS.primaryFontBold};
  font-size: 40px;
  font-weight: 700;
  color: ${T.navy};
  line-height: 1;
`;

const TotalPagesMeta = styled.span`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.base};
  color: ${T.navy};
  line-height: 1.4;
`;

const BundleList = styled.div`
  border: 1.5px solid ${T.line};
  border-radius: 12px;
  overflow: hidden;
  background: white;
`;

const BundleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid ${T.line};
  &:last-child {
    border-bottom: none;
  }
`;

const BundleRowName = styled.span`
  flex: 1;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.sm};
  font-weight: 700;
  color: ${T.navy};
`;

const BundleRowMeta = styled.span`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.mute};
  white-space: nowrap;
`;

const UnassignedBlock = styled.div`
  border-top: 1px dashed ${T.line};
  background: ${T.soft};
`;

const UnassignedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-bottom: 1px solid ${T.line};
`;

const UnassignedTitle = styled.span`
  flex: 1;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.sm};
  font-weight: 700;
  color: ${T.mute};
`;

const UnassignedDocList = styled.div`
  display: flex;
  flex-direction: column;
`;

const UnassignedDocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px 9px 40px;
  border-bottom: 1px solid ${T.line};
  &:last-child {
    border-bottom: none;
  }
`;

const UnassignedDocName = styled.span`
  flex: 1;
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnassignedDocPages = styled.span`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  color: ${T.mute};
  white-space: nowrap;
`;
